import React, { useState, useEffect } from 'react';
import { X, Search, Users, UserPlus, Check, Loader2, AlertCircle, FolderTree } from 'lucide-react';
import { collection, query, getDocs, doc, updateDoc, arrayUnion, arrayRemove, where, getDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { toast } from 'react-hot-toast';
import { useThemeStore } from '../../stores/themeStore';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  photoURL?: string;
}

interface Document {
  id: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  sharedWith?: string[];
  userId: string;
  parentId?: string | null;
}

interface DocumentSharingModalProps {
  document: Document;
  onClose: () => void;
}

export default function DocumentSharingModal({ document, onClose }: DocumentSharingModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [sharedUsers, setSharedUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeContents, setIncludeContents] = useState(true);
  const [childDocuments, setChildDocuments] = useState<Document[]>([]);
  const { darkMode } = useThemeStore();

  useEffect(() => {
    loadUsers();
    loadSharedUsers();
    if (document.type === 'folder') {
      loadFolderContents();
    }
  }, []);

  const loadUsers = async () => {
    if (!auth.currentUser) return;

    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const usersList: User[] = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Ne pas inclure l'utilisateur actuel dans la liste
        if (doc.id !== auth.currentUser?.uid) {
          usersList.push({
            id: doc.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role,
            photoURL: data.photoURL
          });
        }
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSharedUsers = async () => {
    if (!auth.currentUser || !document.sharedWith) {
      setSharedUsers([]);
      return;
    }

    try {
      const sharedWithUsers: User[] = [];
      
      for (const userId of document.sharedWith) {
        try {
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            sharedWithUsers.push({
              id: userDoc.id,
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              photoURL: userData.photoURL
            });
          }
        } catch (error) {
          console.error(`Error loading shared user ${userId}:`, error);
        }
      }
      
      setSharedUsers(sharedWithUsers);
    } catch (error) {
      console.error('Error loading shared users:', error);
      setError('Erreur lors du chargement des utilisateurs partagés');
    }
  };

  const loadFolderContents = async () => {
    if (!auth.currentUser) return;

    try {
      // Query all documents that have this folder as parent
      const q = query(
        collection(db, 'documents'),
        where('parentId', '==', document.id)
      );
      
      const querySnapshot = await getDocs(q);
      const contents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Document[];
      
      // Recursively get all files in subfolders
      const allContents = [...contents];
      const folders = contents.filter(doc => doc.type === 'folder');
      
      for (const folder of folders) {
        const subContents = await getRecursiveFolderContents(folder.id);
        allContents.push(...subContents);
      }
      
      setChildDocuments(allContents);
    } catch (error) {
      console.error('Error loading folder contents:', error);
      setError('Erreur lors du chargement du contenu du dossier');
    }
  };

  const getRecursiveFolderContents = async (folderId: string): Promise<Document[]> => {
    try {
      const q = query(
        collection(db, 'documents'),
        where('parentId', '==', folderId)
      );
      
      const querySnapshot = await getDocs(q);
      const contents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Document[];
      
      let allContents = [...contents];
      
      // Recursively get contents of subfolders
      const folders = contents.filter(doc => doc.type === 'folder');
      for (const folder of folders) {
        const subContents = await getRecursiveFolderContents(folder.id);
        allContents = [...allContents, ...subContents];
      }
      
      return allContents;
    } catch (error) {
      console.error('Error getting recursive folder contents:', error);
      return [];
    }
  };

  const handleShareDocument = async (user: User) => {
    if (!auth.currentUser) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Mettre à jour le document pour ajouter l'utilisateur à la liste de partage
      const docRef = doc(db, 'documents', document.id);
      await updateDoc(docRef, {
        sharedWith: arrayUnion(user.id)
      });
      
      // Si c'est un dossier et que l'option est activée, partager également tous les fichiers à l'intérieur
      if (document.type === 'folder' && includeContents && childDocuments.length > 0) {
        let sharedCount = 0;
        
        for (const childDoc of childDocuments) {
          try {
            const childRef = doc(db, 'documents', childDoc.id);
            await updateDoc(childRef, {
              sharedWith: arrayUnion(user.id)
            });
            sharedCount++;
          } catch (childError) {
            console.error(`Error sharing child document ${childDoc.id}:`, childError);
          }
        }
        
        toast.success(`Dossier et ${sharedCount} fichier(s) partagés avec ${user.email}`);
      } else {
        toast.success(`Document partagé avec ${user.email}`);
      }
      
      // Mettre à jour l'état local
      setSharedUsers([...sharedUsers, user]);
    } catch (error) {
      console.error('Error sharing document:', error);
      setError('Erreur lors du partage du document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveShare = async (user: User) => {
    if (!auth.currentUser) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Mettre à jour le document pour retirer l'utilisateur de la liste de partage
      const docRef = doc(db, 'documents', document.id);
      await updateDoc(docRef, {
        sharedWith: arrayRemove(user.id)
      });
      
      // Si c'est un dossier et que l'option est activée, retirer également le partage de tous les fichiers à l'intérieur
      if (document.type === 'folder' && includeContents && childDocuments.length > 0) {
        let removedCount = 0;
        
        for (const childDoc of childDocuments) {
          try {
            const childRef = doc(db, 'documents', childDoc.id);
            await updateDoc(childRef, {
              sharedWith: arrayRemove(user.id)
            });
            removedCount++;
          } catch (childError) {
            console.error(`Error removing share for child document ${childDoc.id}:`, childError);
          }
        }
        
        toast.success(`Partage retiré pour le dossier et ${removedCount} fichier(s)`);
      } else {
        toast.success(`Partage retiré pour ${user.email}`);
      }
      
      // Mettre à jour l'état local
      setSharedUsers(sharedUsers.filter(u => u.id !== user.id));
    } catch (error) {
      console.error('Error removing share:', error);
      setError('Erreur lors du retrait du partage');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Exclure les utilisateurs déjà dans la liste de partage
    if (sharedUsers.some(sharedUser => sharedUser.id === user.id)) {
      return false;
    }
    
    // Filtrer par terme de recherche
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
  });

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email.split('@')[0];
  };

  const getUserInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Partager "{document.name}"
            </h2>
            <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className={`mb-4 p-4 ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg flex items-start gap-2`}>
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* Option pour inclure le contenu du dossier */}
          {document.type === 'folder' && (
            <div className={`mb-6 p-4 ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'} rounded-lg`}>
              <div className="flex items-start gap-3">
                <FolderTree className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      Contenu du dossier
                    </h3>
                    <span className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                      ({childDocuments.length} élément{childDocuments.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                    Partager également tous les fichiers à l'intérieur de ce dossier
                  </p>
                  <div className="mt-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={includeContents}
                        onChange={(e) => setIncludeContents(e.target.checked)}
                        className={`rounded border-gray-300 text-blue-600 mr-2 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        Inclure le contenu du dossier
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Information sur la suppression */}
          <div className={`mb-6 p-4 ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-100'} rounded-lg`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Important
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                  Si un utilisateur supprime un fichier ou dossier partagé, cela n'affectera pas vos fichiers originaux.
                  Chaque utilisateur gère sa propre vue des documents partagés.
                </p>
              </div>
            </div>
          </div>

          {/* Liste des utilisateurs avec qui le document est partagé */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Partagé avec ({sharedUsers.length})
            </h3>
            
            {sharedUsers.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Ce document n'est partagé avec personne pour le moment.
              </p>
            ) : (
              <div className="space-y-2">
                {sharedUsers.map(user => (
                  <div 
                    key={user.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={getUserDisplayName(user)} 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {getUserInitials(user)}
                        </div>
                      )}
                      <div>
                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {getUserDisplayName(user)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {user.email} {user.role && `• ${user.role}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveShare(user)}
                      disabled={isSaving}
                      className={`px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recherche d'utilisateurs */}
          <div>
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Ajouter des utilisateurs
            </h3>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg`}
              />
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <div 
                      key={user.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                    >
                      <div className="flex items-center gap-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={getUserDisplayName(user)} 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            {getUserInitials(user)}
                          </div>
                        )}
                        <div>
                          <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {getUserDisplayName(user)}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.email} {user.role && `• ${user.role}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleShareDocument(user)}
                        disabled={isSaving}
                        className={`flex items-center px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Partager
                      </button>
                    </div>
                  ))
                ) : (
                  <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {searchTerm ? 'Aucun utilisateur trouvé' : 'Commencez à taper pour rechercher des utilisateurs'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} rounded-lg`}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}